import { forwardRef, useCallback, useEffect, useState, useImperativeHandle, useRef, useMemo } from "react";
import DataGrid from "./DataGrid";
import HeaderCell from "./cells/HeaderCell";
import PropTypes from 'prop-types';
import { GridColumn } from "@progress/kendo-react-grid";
import flags from "./flags";
import _ from "lodash";
import moment from 'moment'
import { getPrimaryField, isInvalid, copyToClipboard, eachDeep, removeAccents } from "../utils/helper";
import { getVisibleColumns } from "./gridHelper";
import ValidationAlert from "../ValidationAlert";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { buildSaveDiscardText } from "../utils/helper";

import { Popup } from '@progress/kendo-react-popup';
import { Menu, MenuItem } from '@progress/kendo-react-layout';

import { editCommandCell } from "./cells/EditCommandCell";
import { useBlockerRegistration } from "../utils/BlockerProvider";

const DATE_FORMATS = ['DD.MM.YYYY', 'D.M.YYYY', 'YYYYMMDD', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ'];

let alertTimeout = null;
let _preventKey = false;
let _contextMenuItem;
let _contextMenuBlurTimeoutRef;
let _contextMenuWrapperRef;


/**
 * ExtendedDataGrid component
 * 
 * @component
 */
const ExtendedDataGrid = forwardRef(function ExtendedDataGrid(props, ref) {
  const {
    cellSelect,
    contextMenu,
    data,
    detail,
    disabled,
    discardText,
    editableCell,
    enterEdit: propsEnterEdit,
    exitEdit: propsExitEdit,
    formatData: propsFormatData,
    hidden,
    isInvalid: propsIsInvalid,
    modified,
    multiple,
    oldData,
    onAbortChanges,
    onAbortChangesAll,
    onAdd,
    onContextMenuSelect,
    onCopy,
    onCreate,
    onExpandChange,
    onItemChange,
    onPaste,
    onSelect,
    onSubmit,
    onUpdate,
    onUpdateAll,
    parseValue: propsParseValue,
    primaryField,
    saveChangesDialogText,
    schema,
    selected = [],
    setData,
  } = props;

  const gridRef = useRef(null);
  const dataPrimaryField = getPrimaryField(primaryField);

  const [isOpenDiscard, setIsOpenDiscard] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  const dataRef = useRef(null);
  dataRef.current = data;
  const propsRef = useRef(props);
  propsRef.current = props;
  const oldDataRef = useRef(null);
  oldDataRef.current = oldData;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectedCellsRef = useRef([]);

  const contextOffset = useRef({ left: 0, top: 0 });
  const [contextMenuPopupOpen, setContextMenuPopupOpen] = useState(false);
  const [dataInvalid, setDataInvalid] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);

  const edit = useMemo(() => {
    const newEdit = data.reduce((acc, item) => {
      if (item.inEdit) {
        acc[item[dataPrimaryField]] = item.inEdit === true ? true : [item.inEdit];
      }
      return acc;
    }, {});

    return newEdit;
  }, [data, dataPrimaryField]);

  const columns = useMemo(() => {
    let columns = [];
    eachDeep(schema, null, (item, parent) => {
      if (item.filter !== 'group' && item.field && (!parent || !['container', 'multipicker', 'enum', 'radio', 'formdatatable'].includes(parent.filter))) {
        columns.push(item);
      }
    });
    return columns;
  }, [schema])

  const visibleColumns = useMemo(() => getVisibleColumns(columns), [columns])

  const isHidden = useCallback((flag) => (!!(hidden & flag)), [hidden]);
  const isDisabled = useCallback((flag) => (!!(disabled & flag)), [disabled]);
  const isHiddenORDisabled = useCallback((flag) => (isHidden(flag) || isDisabled(flag)), [isHidden, isDisabled])

  const isInlineEdit = useMemo(() => !isHiddenORDisabled(flags.GRID_INLINE_EDIT) && !isHiddenORDisabled(flags.GRID) && isHiddenORDisabled(flags.GRID_CELL_EDIT), [isHiddenORDisabled])

  const selectable = useMemo(() => ({
    cell: cellSelect ? true : false
  }), [cellSelect])

  const getColOffset = useCallback(() => {
    return (isHidden(flags.COLUMN_MENU) ? 0 : 1) + (multiple && (!isHidden(flags.COLUMN_SELECTED)) ? 1 : 0) + (detail ? 1 : 0);
  }, [detail, multiple, isHidden])

  const cellKey = (id, columnIndex) => `${id}-${columnIndex}`;

  const getSelCells = useCallback((selected) => {
    const offset = getColOffset()
    let currentCells = [];
    selected.forEach(row => {
      const id = row[dataPrimaryField];
      const rowIndex = dataRef.current.findIndex(r => r[dataPrimaryField] === id);
      if (Array.isArray(row.selected)) {
        row.selected.forEach(columnIndex => {
          const field = visibleColumns[columnIndex - offset].field;
          currentCells.push({ key: cellKey(id, columnIndex), id, rowIndex, columnIndex, field });
        });
      }
    });
    return currentCells;
  }, [dataPrimaryField, visibleColumns, getColOffset]);

  const getSelectedCells = () => {
    const oldSelectedCells = selectedCellsRef.current ?? [];
    const oldKeys = new Set(oldSelectedCells.map(c => c.key));
    const currentCells = getSelCells(selected);
    const currentKeys = new Set(currentCells.map(cell => cell.key));
    const ordered = oldSelectedCells.filter((c) => currentKeys.has(c.key));
    currentCells.forEach(c => {
      if (!oldKeys.has(c.key)) ordered.push(c);
    });

    return ordered;
  }

  selectedCellsRef.current = getSelectedCells();

  const getCurrentCell = useCallback(() => {
    const cellSelectedList = selectedCellsRef.current
    return cellSelectedList.at(-1);
  }, [])

  const parseValue = useCallback((textValue, dataItem, column) => {
    if (propsParseValue) {
      return propsParseValue(textValue, dataItem, column)
    }

    let value = textValue;
    if (value === undefined || value === null) return undefined;

    switch (column.filter) {
      case 'numeric':
      case 'unitnumeric':
        value = value.replace(',', '.');
        value = value.match(/\./) ? parseFloat(value) : parseInt(value);
        return isNaN(value) ? undefined : value;
      case 'date':
      case 'datetime':
      case 'dateinput':
      case 'datetimeinput':
      case '18':
      case '20': {
        const m = moment(value, DATE_FORMATS, true);
        return m.isValid() ? m.toDate() : undefined;
      }
      case 'boolean':
        value = removeAccents(value.toLowerCase());
        return new RegExp(/^[ano|yes|1|true]$/).test(value) ? true :
          new RegExp(/^[nie|no|0|false]$/).test(value) ? false : undefined;
      default:
        return value;
    }
  }, [propsParseValue])

  const isValueChanged = useCallback((dataItem, field) => {
    const oldData = oldDataRef.current;
    const oldDataItem = oldData.find(x => x[dataPrimaryField] === dataItem[dataPrimaryField]);

    for (let j = 0; j < (field ? 1 : visibleColumns.length); j++) {
      const f = field ? field : visibleColumns[j].field;
      const oldValue = oldDataItem ? _.get(oldDataItem, f) : null;
      const newValue = _.get(dataItem, f);
      if (!isEqual(oldValue, newValue)) return true;
    }
    return false;
  }, [dataPrimaryField, visibleColumns])

  const handlePaste = useCallback((e) => {
    if (!editableCell) return
    const clipboardData = e.clipboardData.getData('text/plain');
    if (!clipboardData) return
    const id = Object.keys(edit)[0];
    if (!id) return;
    const field = edit[id]?.[0];
    if (!field) return;

    e.preventDefault();
    let newData = _.cloneDeep(dataRef.current);
    const rows = clipboardData.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n$/, '').split('\n');
    const cellColIndex = visibleColumns.findIndex(col => col.field === field);
    const cellRowIndex = newData.findIndex(r => r[dataPrimaryField] === Number(id))

    for (var i = 0; i < rows.length; i++) {
      const values = rows[i].split('\t');
      for (var j = 0; j < values.length; j++) {
        const rowIndex = cellRowIndex + i;
        const columnIndex = cellColIndex + j;
        const dataItem = newData[rowIndex];
        const column = visibleColumns[columnIndex];
        const canEdit = propsEnterEdit ? propsEnterEdit(dataItem, column.field) : true;
        if (dataItem && column && column.editable && canEdit && !isHiddenORDisabled(flags.UPDATE)) {
          const value = parseValue(values[j].replace(/(\r\n|\n|\r)/g, ''), dataItem, column);
          const hasChanged = isValueChanged({ ...dataItem, [column.field]: value }) ? true : undefined;

          if (value !== undefined) {
            newData[rowIndex][column.field] = value;
            delete newData[rowIndex].hasChanged;
            newData[rowIndex].hasChanged = hasChanged;
          }
        }
      }
    }
    setData(newData);
    if (onPaste) {
      onPaste(newData);
    }
  }, [edit, editableCell, onPaste, propsEnterEdit, isValueChanged, parseValue, visibleColumns, setData, dataPrimaryField, isHiddenORDisabled])

  useEffect(() => {
    const handlePasteWrapper = (e) => {
      // Tu sa volá handlePaste s prístupom k e.clipboardData
      handlePaste(e);
    };

    document.addEventListener('paste', handlePasteWrapper, { capture: true });

    return () => {
      document.removeEventListener('paste', handlePasteWrapper, { capture: true });
    };
  }, [handlePaste]);

  // focusCell  
  useEffect(() => {
    const focusCell = () => {
      if (!gridRef?.current?.ref) { return; }
      const input = gridRef.current.ref.querySelector('td.k-selected.k-grid-edit-cell input');
      const activeElement = document.activeElement;

      if (input) {
        if ((activeElement && input !== activeElement) || !activeElement) {
          if (input.type === 'checkbox') {
            input.focus();
          } else {
            input.select();
          }
        }
      } else {
        gridRef.current.ref.focus();
      }
    };

    const cell = getCurrentCell();
    if (data && cell) {
      focusCell();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(selectedCellsRef.current)])

  const buildEdit = useCallback((event) => {
    if (isInlineEdit) return null
    const select = event.select || {};
    const rowKeys = Object.keys(select);
    // Povoliť edit iba ak je označený presne 1 riadok s presne 1 stĺpcom
    if (!(rowKeys.length === 1 && select[rowKeys[0]]?.length === 1)) return {};
    const rowKey = rowKeys[0];
    const colIndex = select[rowKey][0];
    return { [rowKey]: [visibleColumns[colIndex - getColOffset()].field] };
  }, [visibleColumns, getColOffset, isInlineEdit])

  const exitEdit = useCallback(async (dataItem, field) => {
    const newData = dataRef.current.map(item => { return _.omit(item, ['inEdit']) });
    setData(newData);
    if (propsExitEdit) { propsExitEdit(dataItem, field, newData); }
  }, [propsExitEdit, setData])


  const enterEdit = useCallback(async (dataItem, field) => {
    if (isHiddenORDisabled(flags.UPDATE)) return;
    const multipleCellSelect = selectedCellsRef.current.length > 1;
    const splitField = field ? field.split(".")[0] : field;
    const col = columns.find(x => x.field === field) || columns.find(x => x.filter === "container" && x.field === splitField);

    if (field && col && (dataItem.inEdit === col.field || dataItem.inEdit === true)) { return; }
    const editable = col ? col.editable : true;

    const canEdit = (propsEnterEdit) ? await propsEnterEdit(dataItem, field) : true
    const newData = dataRef.current.map(item => {
      return item[dataPrimaryField] === dataItem[dataPrimaryField] ? ((!multipleCellSelect && canEdit && editable) ?
        { ...item, inEdit: field ? field : true } : { ...item }) : _.omit(item, ['inEdit']);
    });
    
    setData(newData);
  }, [dataPrimaryField, propsEnterEdit, setData, columns, isHiddenORDisabled])

  const handleSelect = useCallback(async (newSelected) => {
    const cells = getSelCells(newSelected);
    if (onSelect) { await onSelect(newSelected, cells); }
  }, [onSelect, getSelCells]);

  const handleEditChange = useCallback(async (event) => {
    const newEdit = event?.edit || buildEdit(event) || {};
    const newId = Object.keys(newEdit)[0];

    if (!_.isEmpty(edit) && editableCell) {
      const oldId = Object.keys(edit)[0];
      if (!(oldId === newId && newEdit[newId]?.[0] === edit[oldId]?.[0])) { 
        const oldDataItem = dataRef.current.find(x => String(x[dataPrimaryField]) === String(oldId));
        await exitEdit(oldDataItem, edit[oldId]?.[0]);
      }
    }   

    if (!newId) return;
    const value = newEdit[newId];
    const dataItem = dataRef.current.find(x => String(x[dataPrimaryField]) === String(newId));
    const field = Array.isArray(value) ? value[0] : null;

    await enterEdit(dataItem, field);
  }, [dataPrimaryField, enterEdit, buildEdit, exitEdit, edit, editableCell])

  const buildText = (id) => {
    return buildSaveDiscardText(id, false, saveChangesDialogText, discardText);
  };

  const abortChanges = useCallback(async (dataItem) => {
    if (onAbortChanges) {
      await onAbortChanges(dataItem);
    }
    else {
      const oldData = oldDataRef.current;
      const originalItem = { ...oldData.find(item => item[dataPrimaryField] === dataItem[dataPrimaryField]) };
      const newData = dataRef.current.map(item => item[dataPrimaryField] === originalItem[dataPrimaryField] ? originalItem : item);
      setData(newData);
    }
  }, [onAbortChanges, dataPrimaryField, setData])

  const handleAbortChangesAll = useCallback(() => {
    if (onAbortChangesAll) {
      onAbortChangesAll();
    }
    else {
      const oldData = oldDataRef.current;
      const newData = oldData.map(item => (_.omit(item, ['inEdit', 'hasChanged'])))
      setData(newData);
    }
  }, [onAbortChangesAll, setData])

  const handleOpenDiscard = useCallback(() => { setIsOpenDiscard(true); }, [])
  const handleCloseDiscard = useCallback(() => {
    setIsOpenDiscard(false);
    setTimeout(() => {
      setNextLocation(null);
    }, 0)
  }, [])

  const blockerCondition = useCallback(() => modified && !nextLocation, [modified, nextLocation])
  const blockerFunction = useCallback((newLocation) => {
    setNextLocation(newLocation)
    handleOpenDiscard();
  }, [handleOpenDiscard])
  const proceedBlocker = useBlockerRegistration(blockerCondition, blockerFunction)

  const handleConfirmDiscard = useCallback(() => {
    const lastEditItem = dataRef.current.find(item => item.inEdit !== undefined);
    abortChanges(lastEditItem)
    proceedBlocker()
    handleCloseDiscard();
  }, [handleCloseDiscard, abortChanges, proceedBlocker])

  const isEqual = (value1, value2) => {
    const val1 = (value1 && value1.getTime ? value1.getTime() : value1);
    const val2 = (value2 && value2.getTime ? value2.getTime() : value2);
    return _.isEqual(val1, val2);
  }

  const handleisInvalid = useCallback(async (field, dataItem) => {
    if (field.disabled || field.hidden) {
      return false;
    }

    const value = _.get(dataItem, field.field)
    const invalid = propsIsInvalid ? await propsIsInvalid(field, dataItem, value) : await isInvalid(field, value, true)

    if (invalid) {
      setInvalidFields(prev => ([...prev, `${field.title} - ${(field.validationMessage || "pole je povinné")}`]))
    }

    return invalid
  }, [propsIsInvalid])

  const isInvalidItem = useCallback(async (dataItem) => {
    return (await Promise.all(
      columns.map(async field => await handleisInvalid(field, dataItem))
    )).some(x => x);
  }, [columns, handleisInvalid])

  const update = useCallback(async (dataItem) => {
    const invalid = await isInvalidItem(dataItem);
    if (!invalid) {
      delete dataItem.inEdit;
      if (dataItem[dataPrimaryField] === 0) {
        await onCreate(dataItem);
        //NOTE: getPageNumber in metaGried
      }
      else {
        await onUpdate(dataItem[dataPrimaryField], dataItem);
      }
    } else {
      setDataInvalid(true);
      clearTimeout(alertTimeout)
      alertTimeout = setTimeout(() => {
        setDataInvalid(false);
        setInvalidFields([])
      }, 4500)
    }
  }, [onUpdate, dataPrimaryField, isInvalidItem, onCreate])

  const itemChange = useCallback(async (event) => {
    // const selectionStart = event.nativeEvent?.target.selectionStart;
    // const selectionEnd = event.nativeEvent?.target.selectionEnd;

    if (!event.field) { return; }

    const hasChanged = isValueChanged({ ...event.dataItem, [event.field]: event.value }) ? true : undefined;

    if (onItemChange) {
      await onItemChange({ ...event, hasChanged: hasChanged }, dataRef.current)
    }
    else {
      setData(prevData => (prevData.map(item => item[primaryField] === event.dataItem[primaryField] ?
        { ...item, [event.field]: event.value, hasChanged: true } : item)))
    }

    // if (selectionStart && selectionEnd) {
    //   event.nativeEvent.target.selectionStart = selectionStart;
    //   event.nativeEvent.target.selectionEnd = selectionEnd;
    // }
  }, [primaryField, isValueChanged, onItemChange, setData])

  const EditCommandCell = useMemo(() => editCommandCell({ edit: enterEdit, update: isHiddenORDisabled(flags.UPDATE) ? null : update, abortChanges, editField: "inEdit", disabled: false }), [enterEdit, update, abortChanges, isHiddenORDisabled])

  const extendedBuildColumns = useMemo(() => {
    const cols = [];
    if (isInlineEdit && (!isHiddenORDisabled(flags.UPDATE) || (isHiddenORDisabled(flags.UPDATE) && (onAdd || onCopy)))) {
      cols.push(<GridColumn
        key="akcie"
        field="akcie"
        cells={{
          data: EditCommandCell,
          headerCell: HeaderCell,
        }}
        filterable={false}
        sortable={false}
        reorderable={false}
        orderIndex={1000000}
        locked={true}
        width="200px"
      />);
    }
    return cols;
  }, [EditCommandCell, isInlineEdit, onAdd, onCopy, isHiddenORDisabled])

  const submitNewItem = useCallback(async () => {
    const newItem = dataRef.current.find(x => x[dataPrimaryField] === 0);

    if (newItem !== null && newItem !== undefined) {
      await onCreate(newItem);
    }

    onSubmit();
  }, [dataPrimaryField, onCreate, onSubmit])

  const handleUpdateAll = useCallback(async () => {
    let dataToupdate = [];

    const lastEditItem = dataRef.current.find(item => item.inEdit !== undefined);
    if (lastEditItem) {
      await exitEdit(lastEditItem, lastEditItem.inEdit);
    }

    const item = dataRef.current.find(x => x[dataPrimaryField] === 0);

    const areInvalid = (await Promise.all(
      dataRef.current.filter(x => x.hasChanged && x[dataPrimaryField] !== 0).map(async dataItem => {
        const invalid = await isInvalidItem(dataItem);
        if (!invalid) {
          dataToupdate.push(dataItem);
        }
        return invalid;
      })
    )).some(x => x);

    if (areInvalid) {
      setDataInvalid(true);
      clearTimeout(alertTimeout)
      alertTimeout = setTimeout(() => {
        setDataInvalid(false);
        setInvalidFields([])
      }, 4500)
      return false;
    }
    else {
      if (item) {
        if (editableCell) {
          submitNewItem();
        }
      }
      if (dataToupdate.length > 0) {

        if (onUpdateAll) {
          await onUpdateAll(dataToupdate);
        } else {
          await Promise.all(dataToupdate.map(async (item) => {
            await onUpdate(item[dataPrimaryField], _.omit(item, ['inEdit', 'hasChanged']));
          }));
        }
      }
      else {
        handleAbortChangesAll();
      }
      return true;
    }
  }, [dataPrimaryField, editableCell, exitEdit, handleAbortChangesAll, isInvalidItem, onUpdate, onUpdateAll, submitNewItem])

  const formatData = useCallback((data, columns) => {
    return propsFormatData ? propsFormatData(data, columns) :
      data.map(item => {
        let newItem = { ...item };
        columns.forEach(col => {
          const value = _.get(item, col.field);
          _.set(newItem, col.field, (value instanceof Date ? moment(value).format('DD.MM.YYYY') : (value ? String(value) : '')));
        });
        return newItem;
      })
  }, [propsFormatData])

  const deleteSelectedCells = useCallback(() => {
    let newData = _.cloneDeep(dataRef.current);
    const minCol = getColOffset();

    selectedCellsRef.current.forEach(selectedCell => {
      const dataItem = newData[selectedCell.rowIndex];
      const column = visibleColumns[selectedCell.columnIndex - minCol];
      const edit = propsEnterEdit ? propsEnterEdit(dataItem, column.field) : true;
      const hasChanged = isValueChanged({ ...dataItem, [column.field]: null }) ? true : undefined;

      if (dataItem && column && column.editable && edit) {
        if (dataItem[column.field] !== undefined) {
          newData[selectedCell.rowIndex][column.field] = null;
          newData[selectedCell.rowIndex].hasChanged = hasChanged;
        }
      }
    });

    setData(newData);
  }, [getColOffset, isValueChanged, propsEnterEdit, setData, visibleColumns])


  const handleKeyDown = useCallback(async (e) => {
    let curCell = getCurrentCell();
    const containerDiv = gridRef.current.ref.querySelector(":scope > .k-grid > .k-grid-aria-root > .k-grid-container");

    if (!curCell) {
      return;
    }

    if (_preventKey) {
      e.preventDefault();
      return;
    }

    _preventKey = true;
    setTimeout(() => { _preventKey = false; }, 100);

    const data = dataRef.current;
    let rowIndex = curCell.rowIndex;
    let columnIndex = curCell.columnIndex;
    let dataItem = data[rowIndex];
    let field = curCell.field;
    const column = visibleColumns.find(x => x.field === field)
    const inEdit = _.get(dataItem, "inEdit") === field ? column.editable : _.get(dataItem, "inEdit");

    if (editableCell) {
      if (["Enter", "Tab"].includes(e.key)) {
        if (!inEdit && !isHiddenORDisabled(flags.UPDATE)) {
          enterEdit(dataItem, field);
        }
        else {
          if (!(e.target.ariaExpanded === "true")) {
            await exitEdit(dataItem, field);
          }
        }
      }
      if (e.key === "Escape" && inEdit) {
        await abortChanges(dataItem);
        await exitEdit(dataItem, field);
      }
      if (e.key === "Delete" && !inEdit) {
        deleteSelectedCells();
      }
    }

    //ctrl+c a ctrl+alt+c
    if (e.keyCode === 67 && e.nativeEvent.ctrlKey) {
      const selectedCells = selectedCellsRef.current;
      const selectedColumns = visibleColumns.filter(col => selectedCells.some(cell => cell.field === col.field));
      let newData = '';

      if (e.altKey) {
        selectedColumns.forEach(col => {
          newData += col.title;
          newData += '\t';
        })
        newData = newData.replace(/\t$/, "") + '\r\n';
      }

      formatData(data, selectedColumns).filter(dataItem => selectedCells.some(cell => String(cell.id) === String(dataItem[dataPrimaryField])))
        .forEach(dataItem => {
          selectedColumns.forEach(col => {
            if (selectedCells.some(cell => (String(cell.id) === String(dataItem[dataPrimaryField])) && cell.field === col.field)) {
              newData += _.get(dataItem, col.field);
            }
            newData += '\t';
          })
          newData = newData.replace(/\t$/, "") + '\r\n';
        });

      copyToClipboard(newData);
    }

    //keynav
    let dx = 0;
    let dy = 0;
    if ((!inEdit && !["Enter", "Tab"].includes(e.key)) || (inEdit && ["Enter", "Tab"].includes(e.key))) {
      switch (e.key) {
        case "Tab": dx = e.nativeEvent.shiftKey ? -1 : 1;
          e.preventDefault();
          e.nativeEvent = { ...e.nativeEvent, shiftKey: false };
          break;
        case "Enter": dy = e.nativeEvent.shiftKey ? -1 : 1;
          e.nativeEvent = { ...e.nativeEvent, shiftKey: false };
          break;
        case "ArrowLeft": dx = -1;
          break;
        case "ArrowRight": dx = 1;
          break;
        case "ArrowUp": dy = -1;
          break;
        case "ArrowDown": dy = 1;
          break;
        default: return;
      }

      const minCol = getColOffset();
      const maxCol = visibleColumns.length - 1 + minCol;

      if (columnIndex + dx < minCol || columnIndex + dx > maxCol) dx = 0;
      if (rowIndex + dy < 0 || rowIndex + dy > data.length - 1) dy = 0;

      columnIndex += dx;
      rowIndex += dy;
      dataItem = data[rowIndex];
      field = visibleColumns[columnIndex - minCol].field;

      let newSelectedItem = selectedRef.current.find(x => x[dataPrimaryField] === dataItem[dataPrimaryField]);
      if (newSelectedItem) {
        newSelectedItem = { ...newSelectedItem, selected: [columnIndex] }
      }
      else {
        newSelectedItem = { ...dataItem, selected: [columnIndex] }
      }
      await handleSelect([newSelectedItem]);

      // custom v/h-scroll for grid
      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        const div = containerDiv.querySelector(':scope > .k-virtual-content');
        const firstCells = [...div.querySelectorAll('tr.k-master-row')[0].cells];
        const cell = div.querySelector('tr.k-master-row td.k-selected');
        const cellWidth = cell.offsetWidth;
        const cellHeight = cell.offsetHeight;
        const cellOffsetLeft = cell.offsetLeft;
        const cellOffsetTop = cell.offsetTop;
        const firstIndex = firstCells.findIndex(c => !c.className.includes('sticky'));
        const lockedLeftWidth = firstCells[firstIndex].offsetLeft;
        const lockedWidth = firstCells.filter(c => c.className.includes('sticky')).map(c => c.offsetWidth).reduce((a, b) => a + b, 0);
        const unlockedWidth = div.clientWidth - lockedWidth;
        const unlockedLeftWidth = (cellOffsetLeft - div.scrollLeft - lockedLeftWidth);

        if ((unlockedLeftWidth + cellWidth) > unlockedWidth && dx > 0) {
          div.scrollLeft += cellWidth;
        }

        if (unlockedLeftWidth < 0 && dx < 0) {
          div.scrollLeft -= cellWidth;
        }

        if ((cellOffsetTop - div.scrollTop + cellHeight) > div.clientHeight && dy > 0) {
          div.scrollTop += cellHeight;
        }

        if ((cellOffsetTop - div.scrollTop) < 0 && dy < 0) {
          div.scrollTop -= cellHeight;
        }
      }
    }

    if (inEdit && ["Enter", "Tab"].includes(e.key) && (dx !== 0 || dy !== 0) && !isHiddenORDisabled(flags.UPDATE)) {
      await enterEdit(dataItem, field);
    }

  }, [abortChanges, dataPrimaryField, enterEdit, exitEdit, formatData, getColOffset, getCurrentCell, editableCell, handleSelect, visibleColumns, deleteSelectedCells, isHiddenORDisabled])

  //#region context menu
  const handleContextMenu = useCallback(event => {
    handleContextMenuOpen(event.syntheticEvent, event.dataItem);
  }, []);

  const handleContextMenuOpen = (e, dataItem) => {
    _contextMenuItem = dataItem;
    contextOffset.current = { left: e.clientX, top: e.clientY };
    setContextMenuPopupOpen(true);
  }

  const contextMenuOnBlurHandler = () => {
    clearTimeout(_contextMenuBlurTimeoutRef);
    _contextMenuBlurTimeoutRef = setTimeout(contextMenuOnBlurTimeout);
  }

  const contextMenuOnFocusHandler = () => {
    clearTimeout(_contextMenuBlurTimeoutRef);
    _contextMenuBlurTimeoutRef = undefined;
  };

  const contextMenuOnBlurTimeout = () => {
    setContextMenuPopupOpen(false);
    _contextMenuBlurTimeoutRef = undefined;
  };

  const oncontextMenuPopupOpen = () => {
    _contextMenuWrapperRef.querySelector('[tabindex]').focus();
  };

  const handleContextMenuOnSelect = (e) => {
    setContextMenuPopupOpen(false);
    if (onContextMenuSelect) {
      onContextMenuSelect(_contextMenuItem, e.item);
    }
  }
  //#endregion

  // const cellRender = useCallback((tdElement, cellProps) => {
  //   const { dataItem, field } = cellProps;
  //   const splitField = field ? field.split(".")[0] : field;
  //   const rowIndex = dataRef.current.findIndex(x => x[dataPrimaryField] === dataItem[dataPrimaryField]);
  //   const colIndex = visibleColumns.findIndex(x => x.field === field);
  //   const columnIndex = getColOffset() + colIndex;
  //   const column = visibleColumns[colIndex];

  //   const clickCellProps = { dataItem, field, rowIndex, columnIndex }

  //   const additionalProps = (field && splitField === dataItem['inEdit'] && colIndex !== -1) ? {} :
  //     {
  //       onClick: async (e) => {
  //         await onCellClick(e, clickCellProps);
  //       },
  //       onDoubleClick: (e) => {
  //         onCellDoubleClick(e, clickCellProps);
  //       },
  //       onTouchStart: (e) => {
  //         const now = Date.now();
  //         if (now - _lastRowClick < 500 && now - _lastRowClick > 0) {
  //           onCellDoubleClick(e, clickCellProps);
  //         }
  //         _lastRowClick = now;
  //       },
  //       tabIndex: "0"
  //     };

  //   var tdProps = { ...tdElement.props, ...additionalProps };

  //   const newValue = _.get(dataItem, field);
  //   tdProps.className = tdProps.className.replace(" k-state-selected", "");
  //   tdProps.className = tdProps.className.replace(" k-state-changed", "");

  //   if (column && column.locked && !tdProps.className.match(/k-grid-content-sticky/)) {
  //     tdProps.className += ' k-grid-content-sticky';
  //   }
  //   const selectedCells = selectedCellsRef.current;
  //   if (selectedCells && selectedCells.find(item => item.rowIndex === rowIndex && item.columnIndex === columnIndex && cellSelect)) {
  //     tdProps.className += " k-state-selected";
  //   }
  //   if (dataItem.hasChanged && field && field !== 'selected' && isValueChanged(dataItem, field) && dataItem[dataPrimaryField] !== 0) {
  //     tdProps.className += " k-state-changed";
  //   }

  //   const td = cloneElement(tdElement, { ...tdProps, ...additionalProps }, tdElement.props.children);
  //   const tdRendered = propsCellRender ? propsCellRender(td, cellProps) : td;

  //   return <MemoCell tdElement={tdRendered} value={newValue} field={field} Id={dataItem[dataPrimaryField]} />;
  // }, [propsCellRender, isValueChanged, visibleColumns, dataPrimaryField, onCellDoubleClick, onCellClick, cellSelect, getColOffset])

  // const getSelectedCells = useCallback((dataItems, select, oldSelectedCells) => {
  //   const minCol = getColOffset();
  //   const maxCol = visibleColumns.length - 1 + minCol;


  //   let newSelectedCells = oldSelectedCells ? selectedCellsRef.current : [];
  //   for (let i = 0; i < dataItems.length; i++) {
  //     const dataItem = dataItems[i];
  //     const id = dataItem[dataPrimaryField];
  //     const rowIndex = dataRef.current.findIndex(item => item[dataPrimaryField] === id);

  //     newSelectedCells = newSelectedCells.filter(cell => cell.rowIndex !== rowIndex)
  //     if (select) {
  //       for (let j = minCol; j <= maxCol; j++) {
  //         const field = visibleColumns[j - minCol].field;
  //         const cell = { id: id, field: field, rowIndex: rowIndex, columnIndex: j };
  //         newSelectedCells.push(cell);
  //       }
  //     }
  //   }

  //   return newSelectedCells;
  // }, [visibleColumns, dataPrimaryField, getColOffset])

  const isDataChanged = useMemo(() => {
    return (data && data.some((item) => item.hasChanged)) || false
  }, [data])

  useImperativeHandle(ref, () => {
    return {
      ...gridRef.current,
    }
  }, []);

  const validationAlert = useMemo(() => <ValidationAlert
    dataInvalid={dataInvalid}
    invalidFields={invalidFields}
  />, [dataInvalid, invalidFields]);

  const id = selected && selected.length === 1 ? selected[0].dataPrimaryField : null;

  const editable = useMemo(() => ({
    enabled: true,
    mode: editableCell ? "incell" : "inline"
  }), [editableCell])

  return <>
    <DataGrid
      ref={gridRef}
      {...props}
      data={data}
      extendedBuildColumns={extendedBuildColumns}
      editable={editable}
      onItemChange={itemChange}
      isValueChanged={isValueChanged}
      edit={edit}
      onSelect={handleSelect}
      onEditChange={handleEditChange}
      onDetailExpandChange={onExpandChange}
      onAbortChangesAll={handleAbortChangesAll}
      handleKeyDown={handleKeyDown}
      validationAlert={validationAlert}
      onUpdateAll={handleUpdateAll}
      selectable={selectable}
      isDataChanged={isDataChanged}
      navigatable={false}
      onContextMenu={handleContextMenu}
      buildEdit={buildEdit}
      contextMenu={contextMenu &&
        <Popup
          offset={contextOffset.current}
          show={contextMenuPopupOpen}
          open={oncontextMenuPopupOpen}
          popupClass={'popup-content'}
        >
          <div
            onFocus={contextMenuOnFocusHandler}
            onBlur={contextMenuOnBlurHandler}
            tabIndex={-1}
            ref={el => (_contextMenuWrapperRef = el)}
          >
            <Menu vertical={true} style={{ display: 'inline-block' }} onSelect={handleContextMenuOnSelect}>
              {contextMenu.map((item, index) =>
                <MenuItem key={index} text={item.text} />
              )}
            </Menu>
          </div>
        </Popup>
      }
    />

    {isOpenDiscard && <ConfirmDialog
      isOpen={isOpenDiscard}
      onClose={handleCloseDiscard}
      onConfirm={handleConfirmDiscard}
      text={buildText(id)}
      confirmText="Áno"
      cancelText="Nie"
    />}
  </>

});

ExtendedDataGrid.propTypes = {
  /** Boolean indicating if individual cell selection is enabled */
  cellSelect: PropTypes.bool,

  /** Array of rows in the context menu */
  contextMenu: PropTypes.array,

  /** Array of data items to be displayed in the grid */
  data: PropTypes.array.isRequired,

  /** Detail component to be displayed when a row is expanded */
  detail: PropTypes.any,

  /** Number indicating which actions will be disabled, based on flags */
  disabled: PropTypes.number,

  /** Text displayed for the discard changes action */
  discardText: PropTypes.string,

  /** Boolean indicating if cells in the grid are editable */
  editableCell: PropTypes.bool,

  /** Function called at the start of editing to check if the element can be validated */
  enterEdit: PropTypes.func,

  /** Function called after editing is completed */
  exitEdit: PropTypes.func,

  /** Function to format data, used in handleKeyDown when copying records from the grid */
  formatData: PropTypes.func,

  /** Number indicating which actions will be hidden, based on flags */
  hidden: PropTypes.number,

  /** Function to determine if the data is invalid */
  isInvalid: PropTypes.func,

  /** Boolean indicating if the data in the grid has been modified */
  modified: PropTypes.bool,

  /** Boolean indicating if multiple row selection is enabled */
  multiple: PropTypes.bool,

  /** Array of old data items, used for comparison and rollback */
  oldData: PropTypes.array,

  /** Function called to abort changes to the current data item. Used to cancel editing and revert to the original data */
  onAbortChanges: PropTypes.func,

  /** Function called to abort all changes in the data grid. Used to cancel all pending edits and revert the grid to its original state */
  onAbortChangesAll: PropTypes.func,

  /** Function invoked when a new entry is added */
  onAdd: PropTypes.func,

  /** Function called when an item is selected from the context menu */
  onContextMenuSelect: PropTypes.func,

  /** Function invoked when an entry is copied */
  onCopy: PropTypes.func,

  /** Function that triggers the create operation */
  onCreate: PropTypes.func,

  /** Function called when a row's expanded state changes */
  onExpandChange: PropTypes.func,

  /** Function called when an item in the grid changes */
  onItemChange: PropTypes.func,

  /** Function called when data is pasted into the grid */
  onPaste: PropTypes.func,

  /** Function called when a row is selected */
  onSelect: PropTypes.func,

  /** Function called when the data grid is submitted */
  onSubmit: PropTypes.func,

  /** Function that triggers the update operation */
  onUpdate: PropTypes.func,

  /** Function that triggers the update operation for all modified items */
  onUpdateAll: PropTypes.func,

  /** Function that parses values, used for pasting data */
  parseValue: PropTypes.func,

  /** String representing the primary field used as a unique identifier */
  primaryField: PropTypes.string.isRequired,

  /** Text displayed for the save changes dialog */
  saveChangesDialogText: PropTypes.string,

  /** Array of schema objects defining the structure of the data grid */
  schema: PropTypes.array.isRequired,

  /** Array of currently selected data items in the grid */
  selected: PropTypes.array,

  /** Function to set the data items in the grid. Used to update the grid's data state */
  setData: PropTypes.func.isRequired,
}

export default ExtendedDataGrid;